import React, { useState, useMemo, useEffect } from "react";
import { useUser } from "../../../../classes/contexts/UserContext";
import { useMeasurementConfigs } from "../../../../hooks/queries/useMeasurementConfigs";
import { useMeasurements } from "../../../../hooks/queries/useMeasurements";
import "./SpineMeasurements.css";
import IconButton from "../../../../components/iconButton/IconButton";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import useTranslation from "../../../../hooks/i18n/useTranslation";

const SpineMeasurements = ({ selectedTwin, instancesByModality }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  // Get preferences
  const { userPreferences } = useUser();

  // Get measurement configs and measurements
  const { measurementConfigsFrontEnd } = useMeasurementConfigs(selectedTwin);

  // Extract measurement IDs from the twin's instances
  const measurementIds = useMemo(() => {
    if (!selectedTwin || !instancesByModality) return [];

    const ids = [];
    const { DX = [] } = instancesByModality;

    // Find instances that belong to this twin and extract their measurement IDs
    if (selectedTwin.sagittal_dx_instance_id) {
      const sagittalInst = DX.find(
        i => i._id === selectedTwin.sagittal_dx_instance_id
      );
      if (sagittalInst && sagittalInst.measurements) {
        ids.push(...sagittalInst.measurements);
      }
    }

    if (selectedTwin.coronal_dx_instance_id) {
      const coronalInst = DX.find(
        i => i._id === selectedTwin.coronal_dx_instance_id
      );
      if (coronalInst && coronalInst.measurements) {
        ids.push(...coronalInst.measurements);
      }
    }

    // Remove duplicates
    const uniqueIds = [...new Set(ids)];

    return uniqueIds;
  }, [selectedTwin, instancesByModality]);

  // Get measurements using the extracted IDs
  const { measurements } = useMeasurements(measurementIds);

  // Define the spine parameters we want to display using unique IDs from preferences
  const targetMeasurementIds = useMemo(
    () => userPreferences?.spino_pelvic_parameters || [],
    [userPreferences]
  );

  // Debug logging
  useEffect(() => {
    // Check all properties of selectedTwin to find where measurements might be
    if (selectedTwin) {
      Object.keys(selectedTwin).forEach(key => {
        if (
          key.toLowerCase().includes("measure") ||
          key.toLowerCase().includes("data") ||
          Array.isArray(selectedTwin[key])
        ) {
        }
      });
    }
  }, [
    selectedTwin,
    measurementConfigsFrontEnd,
    measurements,
    targetMeasurementIds,
  ]);

  // Get measurement configs by unique identifier
  const measurementConfigsById = useMemo(() => {
    if (!measurementConfigsFrontEnd) return {};

    const configsById = {};
    Object.values(measurementConfigsFrontEnd).forEach(categoryConfigs => {
      categoryConfigs.forEach(config => {
        configsById[config.key] = config;
      });
    });

    return configsById;
  }, [measurementConfigsFrontEnd]);

  // Get measurements by unique identifier
  const measurementValuesById = useMemo(() => {
    const valuesById = {};
    measurements.forEach(measurement => {
      valuesById[measurement.unique_identifier] = measurement.value;
    });

    return valuesById;
  }, [measurements]);

  // Find configs for our target measurements
  const displayMeasurements = useMemo(() => {
    if (!measurementConfigsById) return [];

    const found = [];

    targetMeasurementIds.forEach(uniqueId => {
      const config = measurementConfigsById[uniqueId];
      const value = measurementValuesById[uniqueId];

      if (config) {
        found.push({
          shortName: config.shortName,
          fullName: config.name,
          value: value !== undefined ? value : "-",
          unit: config.unit || "",
          key: uniqueId,
        });
      } else {
        // Add placeholder if config not found
        found.push({
          shortName: `ID_${uniqueId}`,
          fullName: `Measurement ${uniqueId}`,
          value: value !== undefined ? value : "-",
          unit: "",
          key: uniqueId,
        });
      }
    });

    return found;
  }, [measurementConfigsById, measurementValuesById, targetMeasurementIds]);

  // Check if there are any valid measurements (not just placeholders with "-" values)
  const hasValidMeasurements = useMemo(() => {
    return displayMeasurements.some(
      measurement =>
        measurement.value !== "-" &&
        measurement.value !== null &&
        measurement.value !== undefined
    );
  }, [displayMeasurements]);

  const formatValue = (value, unit) => {
    if (value === "-" || value === null || value === undefined) {
      return "-";
    }

    if (typeof value === "number") {
      return value.toFixed(1);
    }

    return value.toString();
  };

  // Don't render anything if there are no valid measurements
  if (!hasValidMeasurements) {
    return null;
  }

  return (
    <div className="SpineMeasurements-container">
      <div className="SpineMeasurements-item">
        <div className="SpineMeasurements-header">
          <div className="App-text-large">Spine Parameters</div>
          <IconButton
            icon={isExpanded ? IconsEnum.ARROWUP : IconsEnum.ARROWDOWN}
            onClick={() => setIsExpanded(!isExpanded)}
            noBgColor
            title={isExpanded ? t("434") : t("433")}
          />
        </div>

        {isExpanded && (
          <div className="SpineMeasurements-body">
            <div className="SpineMeasurements-details">
              {displayMeasurements.map((measurement, index) => (
                <div
                  key={measurement.key || index}
                  className="SpineMeasurements-detail-item App-text-small"
                >
                  <span className="SpineMeasurements-label">
                    {measurement.shortName}:
                  </span>
                  <span className="SpineMeasurements-value">
                    {formatValue(measurement.value)}
                  </span>
                  {measurement.unit && (
                    <span className="SpineMeasurements-unit">
                      {measurement.unit}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Export a helper function to check if measurements have data
export const hasSpineMeasurementsData = (
  selectedTwin,
  instancesByModality,
  measurementConfigsFrontEnd,
  measurements,
  targetMeasurementIds = []
) => {
  if (!selectedTwin || !instancesByModality) return false;

  const measurementIds = [];
  const { DX = [] } = instancesByModality;

  // Extract measurement IDs
  if (selectedTwin.sagittal_dx_instance_id) {
    const sagittalInst = DX.find(
      i => i._id === selectedTwin.sagittal_dx_instance_id
    );
    if (sagittalInst && sagittalInst.measurements) {
      measurementIds.push(...sagittalInst.measurements);
    }
  }

  if (selectedTwin.coronal_dx_instance_id) {
    const coronalInst = DX.find(
      i => i._id === selectedTwin.coronal_dx_instance_id
    );
    if (coronalInst && coronalInst.measurements) {
      measurementIds.push(...coronalInst.measurements);
    }
  }

  if (measurementIds.length === 0) return false;

  // Check if any measurements have valid values
  const measurementValuesById = {};

  measurements.forEach(measurement => {
    measurementValuesById[measurement.unique_identifier] = measurement.value;
  });

  return targetMeasurementIds.some(uniqueId => {
    const value = measurementValuesById[uniqueId];
    return value !== undefined && value !== null && value !== "-";
  });
};

export default React.memo(SpineMeasurements);
